/**
 * useHeatmapLayer — Sprint 4.2B.
 *
 * Gerencia uma única camada de heatmap no mapa. Lifecycle:
 *   - mode === 'none' → remove a layer
 *   - mode !== 'none' && pontos > 0 → adiciona / atualiza
 *   - mode trocou → remove a antiga, cria a nova
 *
 * Pesos por modo:
 *
 *   RSSI:        peso = clamp((rssi-(-95)) / (-50-(-95)), 0..1)
 *                grad: 0=vermelho, 0.5=amarelo, 1=verde
 *   Offline:     peso = 1.0 fixo (presença é o sinal)
 *                grad: amarelo→vermelho
 *   Events:      peso = clamp(count / 10, 0..1)
 *                grad: azul→roxo→rosa
 *   Relay OFF:   peso = 1.0 fixo
 *                grad: amarelo→laranja
 *
 * Densidade visual via `radius` e `blur` do plugin.
 */
import { useEffect, useRef } from "react";

import { readDeviceStateEvents } from "./useDeviceStateChanges.js";

const RSSI_MIN = -95;   // peso 0
const RSSI_MAX = -50;   // peso 1

const GRADIENTS = {
  rssi: {
    0.0: "#FF5063",
    0.4: "#FFB224",
    0.7: "#84cc16",
    1.0: "#22c55e",
  },
  offline: {
    0.2: "rgba(234,179,8,0.6)",
    0.5: "rgba(239,68,68,0.7)",
    1.0: "rgba(239,68,68,1)",
  },
  events: {
    0.2: "#60a5fa",
    0.5: "#a78bfa",
    1.0: "#f472b6",
  },
  relay_off: {
    0.2: "rgba(234,179,8,0.6)",
    0.5: "rgba(249,115,22,0.8)",
    1.0: "rgba(249,115,22,1)",
  },
};

const HEAT_OPTS = {
  rssi:      { radius: 32, blur: 22, maxZoom: 17, minOpacity: 0.35 },
  offline:   { radius: 36, blur: 28, maxZoom: 17, minOpacity: 0.45 },
  events:    { radius: 28, blur: 20, maxZoom: 17, minOpacity: 0.35 },
  relay_off: { radius: 32, blur: 24, maxZoom: 17, minOpacity: 0.45 },
};

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function buildPoints(mode, devices) {
  const pts = [];
  if (mode === "rssi") {
    for (const d of devices) {
      if (d.lat == null || d.lng == null) continue;
      if (d.rssi == null || !d.online) continue;
      const w = clamp((d.rssi - RSSI_MIN) / (RSSI_MAX - RSSI_MIN), 0, 1);
      pts.push([d.lat, d.lng, Math.max(0.05, w)]);
    }
  } else if (mode === "offline") {
    for (const d of devices) {
      if (d.lat == null || d.lng == null) continue;
      if (d.online) continue;
      pts.push([d.lat, d.lng, 1.0]);
    }
  } else if (mode === "relay_off") {
    for (const d of devices) {
      if (d.lat == null || d.lng == null) continue;
      if (!d.online) continue;
      if (d.relay !== "off") continue;
      pts.push([d.lat, d.lng, 1.0]);
    }
  } else if (mode === "events") {
    for (const d of devices) {
      if (d.lat == null || d.lng == null) continue;
      const events = readDeviceStateEvents(d.device_id);
      const count = events.length;
      if (count === 0) continue;
      const w = clamp(count / 10, 0.1, 1);
      pts.push([d.lat, d.lng, w]);
    }
  }
  return pts;
}

/**
 * @param {object} L          window.L
 * @param {object} mapRef     useRef pro mapa
 * @param {string} mode       'none' | 'rssi' | 'offline' | 'events' | 'relay_off'
 * @param {Array}  devices    lista de FleetMapDeviceOut
 * @param {bool}   ready      true quando plugin leaflet.heat carregou
 */
export function useHeatmapLayer({ L, mapRef, mode, devices, ready }) {
  const layerRef = useRef(null);
  const lastModeRef = useRef("none");

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !L) return;

    // none → remove
    if (mode === "none" || !ready) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      lastModeRef.current = "none";
      return;
    }

    if (!L.heatLayer) return;   // plugin ainda não carregou

    const pts = buildPoints(mode, devices || []);

    // Se mode mudou ou layer nunca foi criada → recria
    if (!layerRef.current || lastModeRef.current !== mode) {
      if (layerRef.current) map.removeLayer(layerRef.current);
      const opts = HEAT_OPTS[mode] || HEAT_OPTS.rssi;
      layerRef.current = L.heatLayer(pts, {
        ...opts,
        gradient: GRADIENTS[mode],
      });
      layerRef.current.addTo(map);
    } else {
      // mesmo mode → update points
      layerRef.current.setLatLngs(pts);
    }
    lastModeRef.current = mode;

    return undefined;
  }, [L, mapRef, mode, devices, ready]);

  // Cleanup no unmount
  useEffect(() => {
    return () => {
      const map = mapRef?.current;
      if (map && layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [mapRef]);
}
